import { useState, type ReactNode } from 'react'
import { getProxiedImageUrl } from '../image-proxy'
import { useDevicePixelScale } from '../use-device-pixel-scale'
import type { ExploreProperty } from '../../widgets/explorer/data'
import type { CardTranslate } from './card-parts'

interface CardMediaProps {
  property: ExploreProperty
  /** Photos to show; 0 renders no media at all. */
  limit: number
  aspectRatio: string
  /** Rendered above the slides, inside the media frame (type chip, overlay scrim). */
  children?: ReactNode
  /** Card translation resolver for the slideshow's screen-reader labels;
   *  defaults to the English fallbacks for callers without a store. */
  t?: CardTranslate
}

/**
 * Indicator dots stay a single bounded row: at most MAX_DOTS are shown, in a
 * window that follows the active slide. Unbounded dots wrap into rows tall
 * enough to cover the prev/next arrows on narrow cards (300px @ 20 images).
 * 5 × 24px = 120px fits single-row even in the overlay's chip-reserved band
 * (54% of width) down to ~240px-wide cards; the CSS nowrap+overflow backstop
 * keeps the active (window-centered) dot visible below that.
 */
const MAX_DOTS = 5

function dotWindow(count: number, active: number): number[] {
  const start = count <= MAX_DOTS ? 0 : Math.min(Math.max(active - Math.floor(MAX_DOTS / 2), 0), count - MAX_DOTS)
  const size = Math.min(count, MAX_DOTS)
  return Array.from({ length: size }, (_, i) => start + i)
}

/**
 * The photo slideshow shared by every template. Renders nothing when the
 * property has no photos or the limit is 0, so the templates can treat a missing
 * media block as "text-only card". The slideshow controls are real buttons that
 * stack above the card's stretched link (never inside it), so they stay valid
 * HTML and keyboard-operable.
 */
export default function CardMedia({ property, limit, aspectRatio, children, t = (_k, fallback) => fallback ?? '' }: CardMediaProps) {
  const [current, setCurrent] = useState(0)
  // Request images at the device pixel ratio (capped 2×) so they stay crisp on
  // retina. Hydration-safe: starts at the SSR value and refines post-mount.
  const scale = useDevicePixelScale()

  const images = property.images.slice(0, Math.max(0, limit))
  if (images.length === 0) return null

  const index = current < images.length ? current : 0
  const hasMultiple = images.length > 1

  const step = (delta: number) => () => {
    // Clamp before stepping: `prev` can point past the end after the image list
    // shrinks (e.g. a lowered maxImages setting re-renders with fewer slides).
    setCurrent(prev => {
      const from = prev < images.length ? prev : 0
      return (from + delta + images.length) % images.length
    })
  }

  return (
    <div className="hr-pc-media" style={{ aspectRatio }}>
      <div className="hr-pc-media__track">
        {images.map((img, i) => (
          <img
            key={i}
            className={`hr-pc-media__img${i === index ? ' is-active' : ''}`}
            src={getProxiedImageUrl(img, 600 * scale, 400 * scale)}
            alt={i === index ? property.altText || '' : ''}
            aria-hidden={i === index ? undefined : true}
            loading="lazy"
          />
        ))}
      </div>

      {hasMultiple && (
        <>
          <button
            type="button"
            className="hr-pc-media__nav hr-pc-media__nav--prev"
            aria-label={t('photo_previous', 'Previous photo')}
            onClick={step(-1)}
          />
          <button
            type="button"
            className="hr-pc-media__nav hr-pc-media__nav--next"
            aria-label={t('photo_next', 'Next photo')}
            onClick={step(1)}
          />
          <span className="hr-pc-media__dots">
            {dotWindow(images.length, index).map(i => (
              <button
                key={i}
                type="button"
                className={`hr-pc-media__dot${i === index ? ' is-active' : ''}`}
                aria-label={t('photo_position', 'Photo %1 of %2')
                  .replace('%1', String(i + 1))
                  .replace('%2', String(images.length))}
                aria-current={i === index || undefined}
                onClick={() => setCurrent(i)}
              />
            ))}
          </span>
        </>
      )}

      {children}
    </div>
  )
}
